import { defineStore } from 'pinia'
import {
  transformIncomingTrunks,
  transformOutgoingCta,
  getCountryCodeFromTrunks,
  type CtaData,
} from '@/utils/dataTransformers'
import { useNetworkStore } from '@/stores/network/store'
import * as Api from './api'
import { callWithMeta, createMeta, waitFor, type Meta } from '@/utils/store'
import { useCoreStore } from '@/stores/core/store'
import { useOfficeDesksStore } from '@/stores/officeDesks/store'
import { isMyEasybellCom } from '@/utils/environment'

type State = {
  ctaData: Partial<CtaData>
  meta: Meta<{
    ctaData: undefined
    load: undefined
    update: undefined
    factoryResetCta: undefined
    restartCta: undefined
  }>
}

const getDefaultState = (): State => ({
  ctaData: {},
  meta: createMeta([
    'ctaData',
    'load',
    'update',
    'factoryResetCta',
    'restartCta',
  ]),
})

export const useCtaStore = defineStore('cta', {
  state: getDefaultState,

  getters: {
    get: (state) => state.ctaData,
    getBetaAccess: (state) => state.ctaData.betaAccess,
    getAnalyticsEnabled: (state) => state.ctaData.analyticsEnabled,
    getAnalyticsConsentGiven: (state) => !!state.ctaData.analyticsDpaConsented,
    getAvailableTrunks: (state) =>
      transformIncomingTrunks(state.ctaData.trunks ?? []),
    getEdition: (state) => state.ctaData.edition,
    getHasTcProduct: (state) => state.ctaData.hasTcProduct,
    getGroupTags: (state) => state.ctaData.groupTags,
    getCountryCode: (state) =>
      getCountryCodeFromTrunks(state.ctaData.trunks ?? []),
    getSmsServiceAvailable: (state) => !isMyEasybellCom() ? Number(state.ctaData.customer) < 3980964 : false,
  },

  actions: {
    RESET() {
      this.ctaData = getDefaultState().ctaData
      this.meta.ctaData = getDefaultState().meta.load
    },

    SET_DPA_CONSENT() {
      this.ctaData.analyticsDpaConsented = 'accepted'
    },

    ADD_GROUP_TAG(payload: any, type: any) {
      if (
        !this.ctaData.groupTags?.some(
          (tag: any) => tag.id === payload.group_tag_id
        )
      ) {
        this.ctaData.groupTags?.push({
          color: payload.color,
          id: payload.group_tag_id,
          label: payload.label,
          assignedInCallflows: [],
          assignedToDevice: type === 'device' ? true : false,
        })
      }
    },

    DELETE_GROUP_TAG(payload: any) {
      this.ctaData.groupTags = this.ctaData.groupTags?.filter(
        (tag: any) => tag.id != payload
      )
    },

    UPDATE_GROUP_TAG(payload: any) {
      this.ctaData.groupTags?.forEach((tag: any) => {
        if (tag.id == payload.id) {
          tag.color = payload.color
          tag.label = payload.label
        }
      })
    },

    resetFilter(id: any) {
      localStorage.removeItem('device-filter-' + id)
      localStorage.removeItem('extension-filter-' + id)
    },

    async load({ useCache = true } = {}) {
      const hasCached = ['pending', 'success'].includes(
        this.meta.ctaData.status
      )

      if (useCache && hasCached) {
        await waitFor(() => this.meta.ctaData.status !== 'pending')

        return
      }

      await callWithMeta({
        store: this,
        metaFields: { load: undefined, ctaData: undefined },
        apiCall: async () => await Api.loadCtaSettings(),
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('ctaData')) {
            this.ctaData = result.payload
          }
        },
        onError: ({ result }) => {
          if (result.payload.response?.status === 404) {
            useNetworkStore().HANDLE_CTA_NOT_FOUND_ERROR(result.payload.error)
          } else {
            useNetworkStore().HANDLE_LOADING_ERROR(result.payload.error)
          }
        },
      })
    },

    async update(ctaData: Partial<CtaData>) {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { update: undefined, ctaData: undefined },
        apiCall: async () =>
          await Api.updateCtaSettings(transformOutgoingCta(ctaData)),
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('ctaData')) {
            this.ctaData = result.payload
          }
          if (
            Object.prototype.hasOwnProperty.call(
              ctaData,
              'showVoiptogoofficedesks'
            )
          ) {
            useOfficeDesksStore().load({ useCache: false })
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async factoryResetCta() {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { factoryResetCta: undefined, ctaData: undefined },
        apiCall: async () => await Api.resetCta(),
        onSuccess: ({ isLatest }) => {
          if (isLatest('factoryResetCta')) {
            useCoreStore().reloadAll()
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async restartCta() {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { restartCta: undefined, ctaData: undefined },
        apiCall: async () => await Api.restartCta(),
        onSuccess: ({ isLatest }) => {
          if (isLatest('restartCta')) {
            useCoreStore().reloadAll()
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },
  },
})
